import React, { useState, useCallback } from 'react';
import { ScrollView, Pressable } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { CreditCard, ArrowDownToLine, History } from 'lucide-react-native';

// Types
import type { RootStackScreenProps } from '@/types/navigation';

type TabType = 'prepaid' | 'withdraw' | 'history';

// Components
import { Screen, ScreenHeader, SvgBackground } from '@/components';
import WalletHeader from './components/WalletHeader';
import PrepaidTab from './components/PrepaidTab';
import WithdrawTab from './components/WithdrawTab';
import HistoryTab from './components/HistoryTab';
import { Platform } from 'react-native';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';

// Constants
import { COLORS } from '@/constants/colors';
import {
  HEADER_HEIGHT,
  HEADER_OFFSET_LEFT,
  HEADER_OFFSET_TOP,
  HEADER_BORDER_RADIUS,
  SCROLL_VIEW_PADDING_TOP,
  SCROLL_VIEW_PADDING_BOTTOM,
  SCROLL_VIEW_PADDING_HORIZONTAL,
  ICON_SIZE,
  FONT_SIZE_MD,
  BORDER_RADIUS_MEDIUM,
  getTabBackgroundColor,
  getTabBorderColor,
  getTabTextColor,
  CARD_NUMBER_MIN_LENGTH,
  CARD_EXPIRY_MIN_LENGTH,
  CARD_CVC_MIN_LENGTH,
  BLIK_CODE_LENGTH,
  MIN_WITHDRAWAL_AMOUNT,
  BORDER_WIDTH_THIN,
} from './constants';

type Props = RootStackScreenProps<'Wallet'>;

const WalletScreen: React.FC<Props> = ({ navigation }) => {
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const isMobileLayout = useIsMobileLayout();
  
  const bgColor = isDark ? '#000000' : '#f5f5f5';
  
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('prepaid');
  
  // Topup state
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [cardNumber, setCardNumber] = useState<string>('');
  const [cardExpiry, setCardExpiry] = useState<string>('');
  const [cardCVC, setCardCVC] = useState<string>('');
  const [cardName, setCardName] = useState<string>('');
  const [blikCode, setBlikCode] = useState<string>('');
  
  // Withdraw state
  const [withdrawAmount, setWithdrawAmount] = useState<string>('');
  const [bankAccount, setBankAccount] = useState<string>('');
  const [bankName, setBankName] = useState<string>('');

  // Colors
  const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;
  const subtextColor = isDark ? COLORS.GRAY_400 : COLORS.GRAY_500;

  // Computed values
  const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;
  
  const isCardPaymentValid = selectedPaymentMethod === 'card' 
    ? cardNumber.length >= CARD_NUMBER_MIN_LENGTH && 
      cardExpiry.length >= CARD_EXPIRY_MIN_LENGTH && 
      cardCVC.length >= CARD_CVC_MIN_LENGTH && 
      cardName.length > 0
    : true;
  
  const isBlikValid = selectedPaymentMethod === 'blik' 
    ? blikCode.length === BLIK_CODE_LENGTH
    : true;
  
  const canProceed = !!(finalAmount && finalAmount > 0 && selectedPaymentMethod && isCardPaymentValid && isBlikValid);
  const canWithdraw = parseFloat(withdrawAmount) >= MIN_WITHDRAWAL_AMOUNT && bankAccount.length > 0 && bankName.length > 0;

  // Handlers
  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleAmountSelect = useCallback((amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  }, []);

  const handleCustomAmountFocus = useCallback(() => {
    setSelectedAmount(null);
  }, []);

  const handleCustomAmountChange = useCallback((text: string) => {
    setCustomAmount(text);
    setSelectedAmount(null);
  }, []);

  const handlePayment = useCallback(() => {
    if (canProceed) {
      // TODO: Implement payment logic
      alert(`Płatność ${finalAmount} zł przez ${selectedPaymentMethod}`);
    }
  }, [canProceed, finalAmount, selectedPaymentMethod]);

  const handleWithdraw = useCallback(() => {
    const amount = parseFloat(withdrawAmount);
    if (amount >= MIN_WITHDRAWAL_AMOUNT && bankAccount.length > 0 && bankName.length > 0) {
      // TODO: Implement withdraw logic
      alert(`Wypłata ${amount} zł na konto ${bankAccount}`);
    }
  }, [withdrawAmount, bankAccount, bankName]);

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        {(Platform.OS === 'web' && !isMobileLayout) ? (
          <>
            <ScreenHeader
              title="Portfel"
              onBackPress={handleBack}
            />
            {/* Balance Section - Keep for web */}
            <YStack paddingHorizontal="$4" paddingTop="$4" paddingBottom="$2" alignItems="center" backgroundColor={bgColor}>
              <Text fontSize="$3" color={subtextColor} fontWeight="500">
                Dostępne środki
              </Text>
              <Text fontSize="$6" color={textColor} fontWeight="bold" marginTop="$2">
                1 500 zł
              </Text>
            </YStack>
          </>
        ) : (
          <SvgBackground
            offsetLeft={HEADER_OFFSET_LEFT}
            offsetTop={HEADER_OFFSET_TOP}
            height={HEADER_HEIGHT}
            variant="flat"
            borderBottomLeftRadius={HEADER_BORDER_RADIUS}
            borderBottomRightRadius={HEADER_BORDER_RADIUS}
          >
            <WalletHeader isDark={isDark} textColor={textColor} onBack={handleBack} />
          </SvgBackground>
        )}

      <YStack flex={1}>
        {/* Tab Buttons */}
        <XStack paddingHorizontal="$4" paddingVertical="$3" gap="$3">
          {/* Doładuj */}
          <Pressable onPress={() => setActiveTab('prepaid')} style={{ flex: 1 }}>
            <YStack
              backgroundColor={getTabBackgroundColor(isDark, activeTab === 'prepaid')}
              paddingVertical="$3"
              paddingHorizontal="$3"
              borderRadius={BORDER_RADIUS_MEDIUM}
              alignItems="center"
              gap="$2"
              borderWidth={BORDER_WIDTH_THIN}
              borderColor={getTabBorderColor(isDark, activeTab === 'prepaid')}
            >
              <CreditCard size={ICON_SIZE} color={getTabTextColor(activeTab === 'prepaid', textColor)} />
              <Text fontSize={FONT_SIZE_MD} fontWeight="700" color={getTabTextColor(activeTab === 'prepaid', textColor)}>
                Doładuj
              </Text>
            </YStack>
          </Pressable>

          {/* Wypłać */}
          <Pressable onPress={() => setActiveTab('withdraw')} style={{ flex: 1 }}>
            <YStack
              backgroundColor={getTabBackgroundColor(isDark, activeTab === 'withdraw')}
              paddingVertical="$3"
              paddingHorizontal="$3"
              borderRadius={BORDER_RADIUS_MEDIUM}
              alignItems="center"
              gap="$2"
              borderWidth={BORDER_WIDTH_THIN}
              borderColor={getTabBorderColor(isDark, activeTab === 'withdraw')}
            >
              <ArrowDownToLine size={ICON_SIZE} color={getTabTextColor(activeTab === 'withdraw', textColor)} />
              <Text fontSize={FONT_SIZE_MD} fontWeight="700" color={getTabTextColor(activeTab === 'withdraw', textColor)}>
                Wypłać
              </Text>
            </YStack>
          </Pressable>

          {/* Historia */}
          <Pressable onPress={() => setActiveTab('history')} style={{ flex: 1 }}>
            <YStack
              backgroundColor={getTabBackgroundColor(isDark, activeTab === 'history')}
              paddingVertical="$3"
              paddingHorizontal="$3"
              borderRadius={BORDER_RADIUS_MEDIUM}
              alignItems="center"
              gap="$2"
              borderWidth={BORDER_WIDTH_THIN}
              borderColor={getTabBorderColor(isDark, activeTab === 'history')}
            >
              <History size={ICON_SIZE} color={getTabTextColor(activeTab === 'history', textColor)} />
              <Text fontSize={FONT_SIZE_MD} fontWeight="700" color={getTabTextColor(activeTab === 'history', textColor)}>
                Historia
              </Text>
            </YStack>
          </Pressable>
        </XStack>

        <ScrollView
          contentContainerStyle={{
            paddingTop: SCROLL_VIEW_PADDING_TOP,
            paddingBottom: SCROLL_VIEW_PADDING_BOTTOM,
            paddingHorizontal: SCROLL_VIEW_PADDING_HORIZONTAL,
          }}
          showsVerticalScrollIndicator={false}
        >
          <YStack space="$3">
            {/* Tab Content */}
            {activeTab === 'prepaid' && (
              <PrepaidTab
                isDark={isDark}
                textColor={textColor}
                subtextColor={subtextColor}
                selectedAmount={selectedAmount}
                customAmount={customAmount}
                selectedPaymentMethod={selectedPaymentMethod}
                cardNumber={cardNumber}
                cardExpiry={cardExpiry}
                cardCVC={cardCVC}
                cardName={cardName}
                blikCode={blikCode}
                finalAmount={finalAmount}
                canProceed={canProceed}
                onAmountSelect={handleAmountSelect}
                onCustomAmountFocus={handleCustomAmountFocus}
                onCustomAmountChange={handleCustomAmountChange}
                onSelectPaymentMethod={setSelectedPaymentMethod}
                onCardNumberChange={setCardNumber}
                onCardExpiryChange={setCardExpiry}
                onCardCVCChange={setCardCVC}
                onCardNameChange={setCardName}
                onBlikCodeChange={setBlikCode}
                onPayment={handlePayment}
              />
            )}

            {activeTab === 'withdraw' && (
              <WithdrawTab
                isDark={isDark}
                textColor={textColor}
                subtextColor={subtextColor}
                withdrawAmount={withdrawAmount}
                bankAccount={bankAccount}
                bankName={bankName}
                canWithdraw={canWithdraw}
                onWithdrawAmountChange={setWithdrawAmount}
                onBankAccountChange={setBankAccount}
                onBankNameChange={setBankName}
                onWithdraw={handleWithdraw}
              />
            )}

            {activeTab === 'history' && (
              <HistoryTab isDark={isDark} textColor={textColor} subtextColor={subtextColor} />
            )}
          </YStack>
        </ScrollView>
      </YStack>
      </YStack>
    </Screen>
  );
};

export default WalletScreen;

